import axios from "axios";
import { Waves, Dumbbell, Droplets } from "lucide-react";
import TowerIcon from "@/components/ui/tower-icon";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Group hour limits
export const GROUP_HOUR_LIMITS = {
  "-1": Infinity,  // 10 Pack
  0: 4.5,   // TIO (Try it Out)
  1: 2.0,
  2: 3.5,
  4: 5.5,
  6: 7.5,
  8: 9.5,
  10: 11.5,
  12: 14.5,
  15: Infinity,
};

// Max slot duration (in minutes) per group and category
// Tower is available to all groups with no duration limit (except TIO)
export const GROUP_SLOT_LIMITS = {
  "-1": { dryland: 90, water_only: 90, water_with_dryland: 90, tower: 90 },  // Hourly
  0: { dryland: 60, water_only: 90, water_with_dryland: 90, tower: 0 },  // TIO
  1: { dryland: 30, water_only: 30, water_with_dryland: 30, tower: 240 },
  2: { dryland: 60, water_only: 60, water_with_dryland: 60, tower: 240 },
  4: { dryland: 90, water_only: 90, water_with_dryland: 90, tower: 240 },
  6: { dryland: 90, water_only: 90, water_with_dryland: 90, tower: 240 },
  8: { dryland: 120, water_only: 90, water_with_dryland: 90, tower: 240 },
  10: { dryland: 150, water_only: 120, water_with_dryland: 120, tower: 240 },
  12: { dryland: 180, water_only: 120, water_with_dryland: 120, tower: 240 },
  15: { dryland: 240, water_only: 120, water_with_dryland: 120, tower: 240 },
};

// TIO group constant
export const TIO_GROUP = 0;

// 10 Pack group constant
export const TEN_PACK_GROUP = -1;

// Group display name (returns "TIO" for group 0, "Hourly" for -1, "PHG X" for others)
export const getGroupLabel = (groupNumber) => {
  if (groupNumber === TEN_PACK_GROUP) return "Hourly";
  if (groupNumber === TIO_GROUP) return "TIO";
  return `PHG ${groupNumber}`;
};

// Categories that don't count towards weekly hours
export const EXEMPT_CATEGORIES = ['tower'];

// Location options for time slots
export const LOCATIONS = {
  SRDL: {
    code: "SRDL",
    name: "San Ramon Dryland",
    shortName: "SR Dryland",
    address: "4750 Norris Canyon Road, San Ramon, CA 94583",
    mapQuery: "4750+Norris+Canyon+Road,+San+Ramon,+CA+94583"
  },
  SROP: {
    code: "SROP",
    name: "San Ramon Olympic Pool",
    shortName: "SR Olympic",
    address: "9900 Broadmoor Drive, San Ramon, CA 94583",
    mapQuery: "9900+Broadmoor+Drive,+San+Ramon,+CA+94583"
  },
  DVHS: {
    code: "DVHS",
    name: "Dougherty Valley High School",
    shortName: "DV High School",
    address: "10550 Albion Road, San Ramon, CA 94582",
    mapQuery: "10550+Albion+Road,+San+Ramon,+CA+94582"
  },
  MWOK: {
    code: "MWOK",
    name: "Miwok Aquatic Center",
    shortName: "Miwok",
    address: "1800 Ignacio Blvd, Novato, CA 94949",
    mapQuery: "1800+Ignacio+Blvd,+Novato,+CA+94949"
  }
};

// Tailwind class palette per color (needed as literals for JIT)
export const COLOR_CLASSES = {
  orange: { bgClass: "bg-orange-50", borderClass: "border-orange-200", textClass: "text-orange-800", selectedClass: "bg-orange-500 text-white border-orange-600", dotClass: "bg-orange-500", iconColorClass: "text-orange-600" },
  green:  { bgClass: "bg-green-50",  borderClass: "border-green-200",  textClass: "text-green-800",  selectedClass: "bg-green-500 text-white border-green-600",   dotClass: "bg-green-500",  iconColorClass: "text-green-600" },
  blue:   { bgClass: "bg-blue-50",   borderClass: "border-blue-200",   textClass: "text-blue-800",   selectedClass: "bg-blue-600 text-white border-blue-700",     dotClass: "bg-blue-600",   iconColorClass: "text-blue-600" },
  purple: { bgClass: "bg-purple-50", borderClass: "border-purple-200", textClass: "text-purple-800", selectedClass: "bg-purple-500 text-white border-purple-600", dotClass: "bg-purple-500", iconColorClass: "text-purple-600" },
  red:    { bgClass: "bg-red-50",    borderClass: "border-red-200",    textClass: "text-red-800",    selectedClass: "bg-red-500 text-white border-red-600",       dotClass: "bg-red-500",    iconColorClass: "text-red-600" },
  yellow: { bgClass: "bg-yellow-50", borderClass: "border-yellow-200", textClass: "text-yellow-800", selectedClass: "bg-yellow-500 text-white border-yellow-600", dotClass: "bg-yellow-500", iconColorClass: "text-yellow-600" },
  pink:   { bgClass: "bg-pink-50",   borderClass: "border-pink-200",   textClass: "text-pink-800",   selectedClass: "bg-pink-500 text-white border-pink-600",     dotClass: "bg-pink-500",   iconColorClass: "text-pink-600" },
  indigo: { bgClass: "bg-indigo-50", borderClass: "border-indigo-200", textClass: "text-indigo-800", selectedClass: "bg-indigo-500 text-white border-indigo-600", dotClass: "bg-indigo-500", iconColorClass: "text-indigo-600" },
  teal:   { bgClass: "bg-teal-50",   borderClass: "border-teal-200",   textClass: "text-teal-800",   selectedClass: "bg-teal-500 text-white border-teal-600",     dotClass: "bg-teal-500",   iconColorClass: "text-teal-600" },
  slate:  { bgClass: "bg-slate-50",  borderClass: "border-slate-200",  textClass: "text-slate-800",  selectedClass: "bg-slate-500 text-white border-slate-600",   dotClass: "bg-slate-500",  iconColorClass: "text-slate-600" },
};

export const VALID_CATEGORY_COLORS = Object.keys(COLOR_CLASSES);

// Category labels and colors (DYNAMIC: populated from API at app startup via applyCategories())
// Consumers read CATEGORIES[key] as before; the object is mutated in place when data loads.
export const CATEGORIES = {};

// Replace contents of CATEGORIES from the API response.
// `list` is the array returned by GET /api/categories
export function applyCategories(list) {
  // Clear existing keys
  Object.keys(CATEGORIES).forEach((k) => { delete CATEGORIES[k]; });
  // Sort by sort_order then label
  const sorted = [...list].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.label.localeCompare(b.label));
  sorted.forEach((c) => {
    const palette = COLOR_CLASSES[c.color] || COLOR_CLASSES.slate;
    CATEGORIES[c.key] = {
      label: c.label,
      shortLabel: c.short_label,
      color: c.color,
      exempt: !!c.exempt,
      sortOrder: c.sort_order ?? 0,
      categoryId: c.category_id,
      ...palette,
    };
  });
}

// Format time for display
export const formatTime = (time, options) => {
  const [hours, minutes] = time.split(":");
  const h = parseInt(hours);
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  const suffix = (options && options.hideMeridiem) ? "" : ampm;
  return parseInt(minutes) > 0 ? `${h12}:${minutes}${suffix}` : `${h12}${suffix}`;
};

// Get the am/pm of a given time
export const getMeridiem = (time) => {
  const [hours] = time.split(":");
  return parseInt(hours) >= 12 ? "pm" : "am";
};

// Format a time range, omitting meridiem on the start time if both share the same am/pm
export const formatTimeRange = (start, end) => {
  const sameMeridiem = getMeridiem(start) === getMeridiem(end);
  return `${formatTime(start, { hideMeridiem: sameMeridiem })} - ${formatTime(end)}`;
};

// Get week number for a date
export const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

// Get week start date
export const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

// Format a Date as `YYYY-MM-DD` in the LOCAL timezone (not UTC). Using
// `toISOString()` shifts the date across the midnight boundary in negative
// offsets (e.g., PST at 5pm local returns tomorrow's date).
export const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Get month name
export const getMonthName = (monthStr) => {
  const [year, month] = monthStr.split("-");
  const date = new Date(year, parseInt(month) - 1, 1);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

// Check if selecting a category is allowed based on existing selections
// For TIO, pass allSelections (all selections across all dates for that athlete)
// For TIO's 3rd slot, pass selectionDate (YYYY-MM-DD) to enforce the same-day
// vs. later-day rule visually. Without selectionDate the check falls back to
// "either category is possible somewhere".
export const canSelectCategory = (category, existingCategories, groupNumber = null, allSelections = null, selectionDate = null) => {
  // TIO: sequential rules based on total selections count
  if (groupNumber === TIO_GROUP) {
    if (category === "tower") return false;
    const totalCount = allSelections ? allSelections.length : 0;
    if (totalCount === 0) return category === "water_only";
    if (totalCount === 1) {
      // Dryland must not be on the same day as water_only
      if (category === "dryland" && existingCategories.includes("water_only")) return false;
      return category === "dryland";
    }
    if (totalCount === 2) {
      // 3rd slot: same day as dryland → must be water_with_dryland;
      //          later day than dryland → must be water_only.
      if (selectionDate) {
        const drylandSel = (allSelections || []).find((s) => s.category === "dryland");
        const dlDate = drylandSel ? drylandSel.date : null;
        if (!dlDate) return false;
        if (selectionDate === dlDate) return category === "water_with_dryland";
        if (selectionDate > dlDate) return category === "water_only";
        return false; // before dryland day
      }
      return category === "water_with_dryland" || category === "water_only";
    }
    return false; // Already has 3
  }

  if (category === "dryland") {
    return !existingCategories.includes("water_only");
  }

  if (category === "water_only") {
    return !existingCategories.includes("dryland") && !existingCategories.includes("water_with_dryland");
  }

  if (category === "water_with_dryland") {
    // Group 1 can select water_with_dryland without a dryland session
    if (groupNumber === 1) {
      return !existingCategories.includes("water_only");
    }
    return !existingCategories.includes("water_only") && existingCategories.includes("dryland");
  }

  return true;
};

// API helpers
export const api = {
  // Auth
  login: (data) => axios.post(`${API_URL}/auth/login`, data),
  logout: () => axios.post(`${API_URL}/auth/logout`),
  getMe: () => axios.get(`${API_URL}/auth/me`),
  forgotPassword: (email) => axios.post(`${API_URL}/auth/forgot-password`, { email }),

  // User
  getProfile: () => axios.get(`${API_URL}/users/profile`),
  updateProfile: (data) => axios.put(`${API_URL}/users/profile`, data),
  changePassword: (data) => axios.put(`${API_URL}/users/change-password`, data),
  changeEmail: (data) => axios.put(`${API_URL}/users/change-email`, data),
  forceResetPassword: (data) => axios.put(`${API_URL}/users/force-reset-password`, data),
  getWeeklyHours: (year, week) => axios.get(`${API_URL}/users/weekly-hours`, { params: { year, week } }),

  // Time slots
  getTimeSlots: (params) => axios.get(`${API_URL}/timeslots`, { params }),
  getLockout: () => axios.get(`${API_URL}/lockout`),
  createTimeSlot: (data) => axios.post(`${API_URL}/timeslots`, data),
  updateTimeSlot: (slotId, data) => axios.put(`${API_URL}/timeslots/${slotId}`, data),
  setSlotSession: (slotId, session, locked) => axios.put(`${API_URL}/timeslots/${slotId}/session`, { session, locked }),
  deleteTimeSlot: (slotId) => axios.delete(`${API_URL}/timeslots/${slotId}`),
  bulkImportTimeSlots: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/timeslots/bulk-import`, formData);
  },
  getCSVTemplate: () => axios.get(`${API_URL}/timeslots/template`),

  // Selections
  getSelections: (params) => axios.get(`${API_URL}/selections`, { params }),
  createSelection: (data) => axios.post(`${API_URL}/selections`, data),
  deleteSelection: (selectionId) => axios.delete(`${API_URL}/selections/${selectionId}`),
  exportSchedulePdf: (month) => axios.get(`${API_URL}/selections/export-pdf`, { 
    params: { month },
    responseType: 'blob'
  }),
  exportScheduleIcal: (month) => axios.get(`${API_URL}/selections/export-ical`, { 
    params: { month },
    responseType: 'blob'
  }),
  exportPdf: (month) => axios.get(`${API_URL}/selections/export-pdf`, { params: { month }, responseType: 'blob' }),
  adminExportUserPdf: (userId, month, athleteId) => axios.get(
    `${API_URL}/admin/users/${userId}/schedule-pdf`,
    { params: { month, ...(athleteId ? { athlete_id: athleteId } : {}) }, responseType: 'blob' }
  ),
  exportIcal: (month) => axios.get(`${API_URL}/selections/export-ical`, { params: { month }, responseType: 'blob' }),

  // Admin
  getUsers: () => axios.get(`${API_URL}/admin/users`),
  createUser: (data) => axios.post(`${API_URL}/admin/users`, data),
  updateUser: (userId, data) => axios.put(`${API_URL}/admin/users/${userId}`, data),
  deleteUser: (userId) => axios.delete(`${API_URL}/admin/users/${userId}`),
  resetUserPassword: (userId) => axios.post(`${API_URL}/admin/users/${userId}/reset-password`),
  getUserSelections: (userId, params) => axios.get(`${API_URL}/admin/users/${userId}/selections`, { params }),
  adminCreateSelection: (userId, data) => axios.post(`${API_URL}/admin/users/${userId}/selections`, data),
  adminDeleteSelection: (userId, selectionId) => axios.delete(`${API_URL}/admin/users/${userId}/selections/${selectionId}`),
  getDailyReport: (date) => axios.get(`${API_URL}/admin/reports/daily`, { params: { date } }),
  exportDailyRoster: (date) => axios.get(`${API_URL}/admin/reports/daily/export`, { params: { date }, responseType: 'blob' }),
  getUpcomingReport: (days = 7, startDate = null) => axios.get(`${API_URL}/admin/reports/upcoming`, { params: { days, ...(startDate ? { start_date: startDate } : {}) } }),
  getMonthlyReport: (month) => axios.get(`${API_URL}/admin/reports/monthly`, { params: { month } }),
  getAttendanceReport: (month) => axios.get(`${API_URL}/admin/reports/attendance`, { params: { month } }),
  toggleSelectionAbsent: (selectionId, isAbsent) => axios.put(`${API_URL}/admin/selections/${selectionId}/absent`, { is_absent: isAbsent }),

  // Groups
  getGroups: () => axios.get(`${API_URL}/groups`),

  // Messages
  getMessages: () => axios.get(`${API_URL}/messages`),
  getUnreadCount: () => axios.get(`${API_URL}/messages/unread-count`),
  markMessageRead: (messageId) => axios.put(`${API_URL}/messages/${messageId}/read`),
  markAllMessagesRead: () => axios.put(`${API_URL}/messages/mark-all-read`),
  deleteMessage: (messageId) => axios.delete(`${API_URL}/messages/${messageId}`),

  // Admin Messages
  sendAdminMessage: (data) => axios.post(`${API_URL}/admin/messages`, data),
  getAllMessages: () => axios.get(`${API_URL}/admin/messages`),

  // Slot Requests
  createSlotRequest: (data) => axios.post(`${API_URL}/slot-requests`, data),
  getMySlotRequests: () => axios.get(`${API_URL}/slot-requests`),
  getAdminSlotRequests: (status) => axios.get(`${API_URL}/admin/slot-requests`, { params: status ? { status } : {} }),
  approveSlotRequest: (requestId) => axios.post(`${API_URL}/admin/slot-requests/${requestId}/approve`),
  declineSlotRequest: (requestId, message) => axios.post(`${API_URL}/admin/slot-requests/${requestId}/decline`, { message }),

  // Athletes
  getAthletes: () => axios.get(`${API_URL}/athletes`),
  createAthlete: (data) => axios.post(`${API_URL}/athletes`, data),
  updateAthlete: (athleteId, data) => axios.put(`${API_URL}/athletes/${athleteId}`, data),
  deleteAthlete: (athleteId) => axios.delete(`${API_URL}/athletes/${athleteId}`),
  getDayAttendance: (date) => axios.get(`${API_URL}/day-attendance`, { params: { date } }),
  getDayAttendanceCounts: (start, end) => axios.get(`${API_URL}/day-attendance-counts`, { params: { start, end } }),
  ensureAthlete: () => axios.post(`${API_URL}/athletes/ensure`),
  getUserAthletes: (userId) => axios.get(`${API_URL}/admin/users/${userId}/athletes`),
  adminUpdateAthlete: (athleteId, data) => axios.put(`${API_URL}/admin/athletes/${athleteId}`, data),
  adminCreateAthleteForUser: (userId, data) => axios.post(`${API_URL}/admin/users/${userId}/athletes`, data),

  // Categories
  getCategories: () => axios.get(`${API_URL}/categories`),
  adminCreateCategory: (data) => axios.post(`${API_URL}/admin/categories`, data),
  adminUpdateCategory: (categoryId, data) => axios.put(`${API_URL}/admin/categories/${categoryId}`, data),
  adminDeleteCategory: (categoryId) => axios.delete(`${API_URL}/admin/categories/${categoryId}`),

  // Backup / Restore
  downloadBackup: () => axios.get(`${API_URL}/admin/backup`, { responseType: 'blob' }),
  restoreBackup: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Do NOT set Content-Type manually — the browser needs to set it to
    // `multipart/form-data; boundary=...`. Setting it here strips the
    // boundary and FastAPI rejects the request with "Missing boundary in multipart."
    return axios.post(`${API_URL}/admin/restore`, formData);
  },

  // Admin settings
  getLockoutSetting: () => axios.get(`${API_URL}/admin/settings/lockout-days`),
  updateLockoutSetting: (value) => axios.put(`${API_URL}/admin/settings/lockout-days`, { value }),

  // Dryland-stations layout overrides (admin-only)
  patchSelectionStations: (selectionId, body) =>
    axios.patch(`${API_URL}/admin/selections/${selectionId}/stations`, body),
  resetDrylandStations: (date) =>
    axios.post(`${API_URL}/admin/reports/daily/${date}/reset-stations`),
};

// Re-export cn from shadcn
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}
